/**
 * Android-specific DirectWrite utilities.
 * 
 * On Android, showDirectoryPicker returns read-only handles due to SAF (Storage Access Framework)
 * restrictions. This module provides functions to work around this limitation by using
 * showOpenFilePicker for the write phase.
 */

import { patchExif, PatchMetadata } from './exifUtils';

export type AndroidWriteTarget = {
    id: string;
    /** The processed file's relative path (used for matching with file picker selection) */
    targetRelativePath: string;
    /** The original file's relative path */
    originalRelativePath?: string | null;
    /** Download URL for the processed file from GCS (fallback) */
    downloadUrl: string;
    /** GCS blob path for signed URL resolution */
    blobPath?: string;
    /** Size hint for progress calculation */
    size?: number;
};

export type AndroidWriteResult = {
    id: string;
    targetRelativePath: string;
    originalRelativePath?: string | null;
    status: "written" | "failed" | "skipped";
    method?: "patched" | "downloaded" | "save-picker";
    bytesWritten?: number;
    renamed?: boolean;
    error?: string;
};

export type AndroidWriteOptions = {
    /** Callback for progress updates */
    onProgress?: (processed: number, total: number, current: AndroidWriteResult) => void;
    /** Backend file status with metadata for client-side patching */
    filesStatus?: Record<string, any>;
    /** Function to resolve download URLs (for signed URL fetching) */
    resolveDownloadUrl?: (target: AndroidWriteTarget) => Promise<string | null>;
};

/**
 * Prompts the user to select files for write-back using showOpenFilePicker.
 * Returns a map of filename (lowercase) -> FileSystemFileHandle for matching.
 */
export async function promptForWritableHandles(): Promise<Map<string, FileSystemFileHandle>> {
    const showOpenFilePicker = (window as any).showOpenFilePicker;
    if (typeof showOpenFilePicker !== "function") {
        throw new Error("File picker not available on this device.");
    }

    let handles: FileSystemFileHandle[];
    try {
        handles = await showOpenFilePicker({
            multiple: true,
            types: [
                {
                    description: "Images",
                    accept: {
                        "image/*": [".jpg", ".jpeg", ".webp"],
                    },
                },
            ],
        });
    } catch (err: any) {
        if (err?.name === "AbortError") {
            throw new Error("File selection cancelled.");
        }
        throw err;
    }

    // Build lookup map: filename (lowercase) -> handle
    const handleMap = new Map<string, FileSystemFileHandle>();
    for (const handle of handles) {
        const key = handle.name.toLowerCase();
        handleMap.set(key, handle);
    }

    return handleMap;
}

/**
 * Match targets with selected file handles by filename.
 * Returns matched targets with their writable handles.
 */
export function matchTargetsWithHandles(
    targets: AndroidWriteTarget[],
    handleMap: Map<string, FileSystemFileHandle>
): Array<AndroidWriteTarget & { writeHandle?: FileSystemFileHandle }> {
    return targets.map((target) => {
        // Extract filename from path
        const pathParts = (target.originalRelativePath || target.targetRelativePath).split("/");
        const filename = pathParts[pathParts.length - 1].toLowerCase();

        const handle = handleMap.get(filename);
        return { ...target, writeHandle: handle };
    });
}

/**
 * Extract metadata from filesStatus for client-side patching.
 */
function extractMetadata(statusEntry: any): PatchMetadata | null {
    if (!statusEntry) return null;

    const state = statusEntry.processing_state;
    if (!state) return null;

    let lat: number | undefined;
    let lng: number | undefined;
    let date: string | undefined;

    // Search for coordinates in various possible locations
    const candidates = [
        state.marker,
        state.marker?.final_location,
        state.marker?.finalLocation,
        state.final_location,
        state,
    ];

    for (const c of candidates) {
        if (!c) continue;

        if (typeof c.lat === "number") lat = c.lat;
        else if (typeof c.latitude === "number") lat = c.latitude;

        if (typeof c.lng === "number") lng = c.lng;
        else if (typeof c.longitude === "number") lng = c.longitude;

        if (typeof c.final_date === "string") date = c.final_date;
        else if (typeof c.finalDate === "string") date = c.finalDate;
        else if (typeof c.date === "string") date = c.date;

        // Date object case { date: "..." }
        if (c.final_date && typeof c.final_date.date === "string") {
            date = c.final_date.date;
        }

        if (lat !== undefined && lng !== undefined) break;
    }

    if (lat === undefined && lng === undefined && !date) {
        return null;
    }

    const meta: PatchMetadata = {};
    if (lat !== undefined && lng !== undefined) {
        meta.lat = lat;
        meta.lng = lng;
    }
    if (date) {
        meta.date = date;
    }

    return meta;
}

/**
 * Write a blob to a FileSystemFileHandle.
 */
async function writeBlobToHandle(handle: FileSystemFileHandle, blob: Blob): Promise<number> {
    const writable = await handle.createWritable();
    try {
        await writable.write(blob);
        await writable.close();
        return blob.size;
    } catch (err) {
        try {
            await writable.abort();
        } catch {
            // Ignore abort errors
        }
        throw err;
    }
}

/**
 * Fallback: prompt user with showSaveFilePicker for a single file.
 */
async function saveWithPicker(suggestedName: string, blob: Blob): Promise<number> {
    const showSaveFilePicker = (window as any).showSaveFilePicker;
    if (typeof showSaveFilePicker !== "function") {
        throw new Error("Save file picker not available.");
    }

    const handle = await showSaveFilePicker({
        suggestedName,
        types: [
            {
                description: "JPEG Image",
                accept: { "image/jpeg": [".jpg", ".jpeg"] },
            },
        ],
    });

    return await writeBlobToHandle(handle, blob);
}

/**
 * Main function to write processed files back on Android.
 * 
 * Flow:
 * 1. User selects files via showOpenFilePicker (called separately via promptForWritableHandles)
 * 2. For each target, try to write using the matched handle
 * 3. If no match or write fails, offer showSaveFilePicker fallback
 */
export async function writeToAndroidFileSystem(
    targets: AndroidWriteTarget[],
    handleMap: Map<string, FileSystemFileHandle>,
    options?: AndroidWriteOptions
): Promise<AndroidWriteResult[]> {
    const results: AndroidWriteResult[] = [];
    const filesStatus = options?.filesStatus || {};
    const onProgress = options?.onProgress;
    const resolveDownloadUrl = options?.resolveDownloadUrl;

    const matchedTargets = matchTargetsWithHandles(targets, handleMap);

    for (let idx = 0; idx < matchedTargets.length; idx++) {
        const target = matchedTargets[idx];
        const result: AndroidWriteResult = {
            id: target.id,
            targetRelativePath: target.targetRelativePath,
            originalRelativePath: target.originalRelativePath ?? undefined,
            status: "failed",
        };

        try {
            // Extract filename for display
            const pathParts = target.targetRelativePath.split("/");
            const targetFilename = pathParts[pathParts.length - 1];

            if (!target.writeHandle) {
                // No matching handle - skip or use save picker
                result.status = "skipped";
                result.error = `No matching file selected for "${targetFilename}". Skipped.`;
                results.push(result);
                onProgress?.(idx + 1, matchedTargets.length, result);
                continue;
            }

            // Try client-side patching first
            const statusEntry = filesStatus[target.id];
            const metadata = extractMetadata(statusEntry);
            const shouldRename = target.targetRelativePath !== target.originalRelativePath && target.originalRelativePath;
            let patchedBlob: Blob | null = null;
            let bytesWritten = 0;

            // 1. Prepare Content (Patch or Download)
            if (metadata && (metadata.lat !== undefined || metadata.date)) {
                try {
                    const originalFile = await target.writeHandle.getFile();
                    patchedBlob = await patchExif(originalFile, metadata);
                    console.info(`[AndroidWrite] Client-side patch success: ${targetFilename}`);
                } catch (patchErr) {
                    console.warn(`[AndroidWrite] Patch failed for ${targetFilename}, will try download.`, patchErr);
                }
            }

            let blobToWrite: Blob;
            let method: AndroidWriteResult["method"] = "downloaded";

            if (patchedBlob) {
                blobToWrite = patchedBlob;
                method = "patched";
            } else {
                // Fallback: download processed file
                try {
                    const downloadUrl = resolveDownloadUrl
                        ? await resolveDownloadUrl(target)
                        : target.downloadUrl;

                    if (!downloadUrl) throw new Error("No download URL available.");
                    const response = await fetch(downloadUrl, { cache: "no-store" });
                    if (!response.ok) throw new Error(`Download failed: ${response.status}`);
                    blobToWrite = await response.blob();
                } catch (dlErr: any) {
                    throw new Error(`Download failed: ${dlErr?.message || String(dlErr)}`);
                }
            }

            // 2. Write Content
            bytesWritten = await writeBlobToHandle(target.writeHandle, blobToWrite);
            result.status = "written";
            result.method = method;
            result.bytesWritten = bytesWritten;

            // 3. Attempt Rename if needed
            if (shouldRename) {
                const newName = target.targetRelativePath.split('/').pop();
                // Check for move() support (Chrome 111+ on Desktop, rare on Android)
                // @ts-ignore
                if (newName && typeof target.writeHandle.move === 'function') {
                    try {
                        // @ts-ignore
                        await target.writeHandle.move(newName);
                        result.renamed = true;
                    } catch (moveErr: any) {
                        console.warn(`[AndroidWrite] Rename (move) failed for ${targetFilename}`, moveErr);
                        result.renamed = false;
                        result.error = "Content updated, but Rename failed (Android requires Download to rename)";
                        result.status = "failed"; // Mark as failed because user considers rename essential
                    }
                } else {
                    result.renamed = false;
                    result.error = "Content updated, but Renaming not supported on this device";
                    result.status = "failed"; // Mark as failed to alert user
                }
            } else {
                result.renamed = false;
            }

        } catch (err: any) {
            result.error = err?.message || String(err);
            result.status = "failed";
        }

        results.push(result);
        onProgress?.(idx + 1, matchedTargets.length, result);
    }

    console.info("[AndroidWrite] Write report", results);
    return results;
}

/**
 * Generates user-friendly instructions for android file re-selection.
 */
export function getAndroidReselectionMessage(fileCount: number): string {
    return `To save the ${fileCount} processed ${fileCount === 1 ? "file" : "files"}, ` +
        `please select **ALL** files in the original folder. ` +
        `The app will automatically identify and update only the processed ones. ` +
        `\n(Note: Renaming is not supported on Android)`;
}
